import { useMemo } from 'react'

import { useScraperData } from './useScraperData'

type ImageData = {
  url: string
  width: number
  height: number
  type: string
  opengraph: boolean
}

/**
 * Hook that returns the best thumbnail image for an article URL.
 * Images are sorted by:
 * 1. OpenGraph images first (opengraph: true)
 * 2. Then by size descending (width * height)
 */
export const useArticleThumbnail = (
  articleUrl: string,
): {
  imageUrl: string | undefined
  loading: boolean
  error?: string
} => {
  const { data, loading, error } = useScraperData(articleUrl)

  const imageUrl = useMemo(() => {
    if (!data?.images || data.images.length === 0) {
      return undefined
    }

    // Sort images: OpenGraph first, then by size descending
    const sortedImages = data.images.toSorted((a: ImageData, b: ImageData) => {
      // OpenGraph images come first
      if (a.opengraph && !b.opengraph) return -1
      if (!a.opengraph && b.opengraph) return 1

      // Then sort by size (width * height) descending
      const sizeA = a.width * a.height
      const sizeB = b.width * b.height
      return sizeB - sizeA
    })

    return sortedImages[0]?.url
  }, [data?.images])

  return {
    imageUrl,
    loading,
    error,
  }
}
